import { useEffect, useMemo, useState } from "react"
import { router } from "@inertiajs/react"
import { Construction, Fuel, HardHat, type LucideIcon, Package, Plane, Plus, Receipt, Stamp, Wrench, X } from "lucide-react"
import { cn, formatCurrency } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import type { Expense } from "@/pages/Projects/tabs/types"

type ExpenseType = { key: string; label: string; hint: string; icon: LucideIcon; rental?: boolean }

const TYPES: readonly ExpenseType[] = [
  { key: "Materials",        label: "Materials",        hint: "Steel, fasteners",     icon: Package },
  { key: "Equipment Rental", label: "Equipment Rental", hint: "Crane, lift, gen",     icon: Construction, rental: true },
  { key: "Subcontractor",    label: "Subcontractor",    hint: "Outside crews",        icon: HardHat },
  { key: "Fuel",             label: "Fuel",             hint: "Diesel, gas",          icon: Fuel },
  { key: "Travel",           label: "Travel & Per Diem", hint: "Lodging, meals",      icon: Plane },
  { key: "Permits",          label: "Permits & Fees",   hint: "Licenses, inspection", icon: Stamp },
  { key: "Consumables",      label: "Consumables",      hint: "Blades, rod, PPE",     icon: Wrench },
  { key: "Other",            label: "Other",            hint: "Anything else",        icon: Receipt },
]

const RENTAL_UNITS = ["hour", "day", "week", "month"] as const
type RentalUnit = (typeof RENTAL_UNITS)[number]
const UNIT_ABBR: Record<RentalUnit, string> = { hour: "hr", day: "day", week: "wk", month: "mo" }

const FIELD = "text-meta text-muted-foreground"

function todayISO(): string {
  return new Date().toISOString().slice(0, 10)
}

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  editingExpense: Expense | null
  tagSuggestions: string[]
}

export function ExpenseDialog({ open, onOpenChange, projectId, editingExpense, tagSuggestions }: Props) {
  const [type, setType] = useState<string | null>(null)
  const [amount, setAmount] = useState("")
  const [date, setDate] = useState(todayISO())
  const [vendor, setVendor] = useState("")
  const [description, setDescription] = useState("")
  const [tags, setTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState("")
  const [receiptUrl, setReceiptUrl] = useState("")
  const [equipment, setEquipment] = useState("")
  const [rate, setRate] = useState("")
  const [unit, setUnit] = useState<RentalUnit>("month")
  const [qty, setQty] = useState("1")
  const [billable, setBillable] = useState(true)
  const [markupPct, setMarkupPct] = useState("")

  useEffect(() => {
    if (!open) return
    const e = editingExpense
    setType(e ? (TYPES.find((t) => t.key === e.category)?.key ?? "Other") : null)
    setAmount(e ? String(e.amount) : "")
    setDate(e?.date ?? todayISO())
    setVendor(e?.vendor ?? "")
    setDescription(e?.description ?? "")
    setTags(e?.tags ?? [])
    setReceiptUrl(e?.receiptUrl ?? "")
    setBillable(e?.billable ?? true)
    setMarkupPct(e?.markupPct != null ? String(e.markupPct) : "")
    setTagInput(""); setEquipment(""); setRate(""); setUnit("month"); setQty("1")
  }, [open, editingExpense])

  const selected = TYPES.find((t) => t.key === type)
  const isRentalCalc = !!selected?.rental && !editingExpense
  const computed = (parseFloat(rate) || 0) * (parseFloat(qty) || 0)
  const effectiveAmount = isRentalCalc ? computed : parseFloat(amount) || 0
  const rentalDescription = `${equipment.trim() || "Equipment rental"} — ${qty || 0} ${unit}${Number(qty) === 1 ? "" : "s"} @ ${formatCurrency(parseFloat(rate) || 0)}/${UNIT_ABBR[unit]}`
  const canSave = !!type && effectiveAmount > 0 && !!date

  const addTag = (raw: string) => {
    const t = raw.trim().toLowerCase()
    if (t && !tags.includes(t)) setTags([...tags, t])
    setTagInput("")
  }

  const submit = () => {
    if (!canSave) return
    const payload = {
      date,
      category:    type,
      amount:      effectiveAmount,
      description: isRentalCalc ? rentalDescription : description.trim() || null,
      vendor:      vendor.trim() || null,
      tags,
      receiptUrl:  receiptUrl.trim() || null,
      billable,
      markupPct:   markupPct.trim() || null,
    }
    const opts = { preserveScroll: true, onSuccess: () => onOpenChange(false) }
    if (editingExpense) router.patch(`/expenses/${editingExpense.dbId}`, payload, opts)
    else router.post(`/projects/${projectId}/expenses`, payload, opts)
  }

  const suggestions = useMemo(
    () => tagSuggestions.filter((t) => !tags.includes(t)).slice(0, 6),
    [tagSuggestions, tags],
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editingExpense ? "Edit expense" : "Log expense"}</DialogTitle>
          <DialogDescription>
            Pick what kind of cost this is — only the fields it needs will appear.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
          {TYPES.map((t) => {
            const Icon = t.icon
            const active = type === t.key
            return (
              <button
                key={t.key}
                onClick={() => setType(t.key)}
                className={cn(
                  "flex flex-col items-start gap-2 rounded-lg border p-3 text-left transition-colors",
                  active ? "border-foreground bg-secondary" : "border-border bg-card hover:bg-secondary",
                )}
              >
                <Icon className={cn("h-4 w-4", active ? "text-foreground" : "text-muted-foreground")} />
                <span className="text-body font-medium leading-tight">{t.label}</span>
                <span className="text-meta leading-tight text-muted-foreground">{t.hint}</span>
              </button>
            )
          })}
        </div>

        {type && (
          <div className="flex flex-col gap-5 pt-1">
            {isRentalCalc ? (
              <div className="flex flex-col gap-4 rounded-lg border border-border bg-card p-3.5">
                <div className="flex flex-col gap-2">
                  <label className={FIELD}>Equipment</label>
                  <Input value={equipment} onChange={(e) => setEquipment(e.target.value)} placeholder="Scissor lift, 40ft boom…"  />
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div className="flex flex-col gap-2">
                    <label className={FIELD}>Rate</label>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-body text-muted-foreground">$</span>
                      <Input type="number" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="1200" className="num pl-7" />
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className={FIELD}>Per</label>
                    <div className="flex h-[34px] items-center gap-1 rounded-md border border-border bg-background p-0.5">
                      {RENTAL_UNITS.map((u) => (
                        <button
                          key={u}
                          onClick={() => setUnit(u)}
                          className={cn(
                            "flex-1 rounded-[5px] py-1 text-meta font-medium capitalize transition-colors",
                            unit === u ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
                          )}
                        >
                          {UNIT_ABBR[u]}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className={FIELD}>Quantity</label>
                    <Input type="number" min="0" value={qty} onChange={(e) => setQty(e.target.value)} className="num" />
                  </div>
                </div>
                <div className="flex items-baseline justify-between border-t border-border pt-3">
                  <span className={FIELD}>Total</span>
                  <span className="num text-2xl font-semibold text-whisper">{formatCurrency(computed)}</span>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <label className={FIELD}>Amount</label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-body text-muted-foreground">$</span>
                  <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="num pl-8 text-lg" />
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label className={FIELD}>Date</label>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}  />
              </div>
              <div className="flex flex-col gap-2">
                <label className={FIELD}>{isRentalCalc ? "Rental company" : "Vendor"} <span className="normal-case opacity-60">· optional</span></label>
                <Input value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="US Steel Supply"  />
              </div>
            </div>

            {!isRentalCalc && (
              <div className="flex flex-col gap-2">
                <label className={FIELD}>Description <span className="normal-case opacity-60">· optional</span></label>
                <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What was it for?"  />
              </div>
            )}

            <div className="flex flex-col gap-2">
              <label className={FIELD}>Tags</label>
              <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-border bg-background p-2">
                {tags.map((t) => (
                  <span key={t} className="flex items-center gap-1 rounded-md bg-secondary px-2 py-1 text-meta text-secondary-foreground">
                    {t}
                    <button onClick={() => setTags(tags.filter((x) => x !== t))}><X className="h-3 w-3" /></button>
                  </span>
                ))}
                <input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(tagInput) }
                    else if (e.key === "Backspace" && !tagInput && tags.length) setTags(tags.slice(0, -1))
                  }}
                  placeholder={tags.length ? "" : "lot-a, phase-2, reimbursable…"}
                  className="min-w-[8rem] flex-1 bg-transparent px-1 py-1 text-body outline-none"
                />
              </div>
              {suggestions.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {suggestions.map((t) => (
                    <button key={t} onClick={() => addTag(t)} className="flex items-center gap-1 rounded-md border border-dashed border-border px-2 py-0.5 text-meta text-muted-foreground transition-colors hover:border-foreground hover:text-foreground">
                      <Plus className="h-3 w-3" />{t}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className={FIELD}>Receipt URL <span className="normal-case opacity-60">· optional</span></label>
              <Input value={receiptUrl} onChange={(e) => setReceiptUrl(e.target.value)} placeholder="https://…"  />
            </div>

            <div className="flex items-center justify-between gap-4 border-t border-border pt-3">
              <div className="flex flex-col gap-0.5">
                <span className={FIELD}>Billable to client</span>
                <span className="text-meta normal-case text-muted-foreground">Include this expense on a client invoice</span>
              </div>
              <Switch checked={billable} onCheckedChange={setBillable} />
            </div>
            {billable && (
              <div className="flex flex-col gap-2">
                <label className={FIELD}>Markup % <span className="normal-case opacity-60">· optional, overrides project default</span></label>
                <Input type="number" step="0.01" value={markupPct} onChange={(e) => setMarkupPct(e.target.value)} placeholder="0"  />
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between border-t border-border pt-4">
          <span className="num text-body text-muted-foreground">
            {effectiveAmount > 0 ? formatCurrency(effectiveAmount) : ""}
            {billable && effectiveAmount > 0 && (parseFloat(markupPct) || 0) > 0
              ? ` · bill ${formatCurrency(effectiveAmount * (1 + (parseFloat(markupPct) || 0) / 100))}`
              : ""}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={submit} disabled={!canSave}>{editingExpense ? "Save changes" : "Log expense"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
